import { useEffect, useRef, useState } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";

// Toolkit imports
import {
  fetchExerciseDetails,
  fetchExerciseVariant,
  fetchLessonAttempt,
  fetchLessonResources,
  getAssessment,
  getQuiz,
} from "sj-ui-toolkit/src/api";
import { getContentClient, getExperimentClient } from "sj-ui-toolkit/src/client";
import { convertLessonDetails } from "../helpers/lessons";

const difficultyMapping = {
  "Break/Fix": "Basic",
  Coding_Challenge: "Intermediate",
  Ctf_Challenge: "Advanced",
};

/**
 * Builds the common lesson details object from Rails data, source-specific overrides,
 * tournament content, and assessment data.
 *
 * @param {Object} data - Data from the rails fetchLessonAttempt API
 * @param {Object} overrides - Source-specific field overrides from svc-content
 * @param {Object|null} tournamentContent - Tournament context, if any
 * @returns {Promise<Object>} The assembled lesson details
 */
async function buildLessonDetails(data, overrides, tournamentContent, quizRedesign) {
  const details = {
    id: data.lesson.id,
    type: data.type,
    title: data.lesson.name,
    description: data.lesson.description,
    level: data.levelName,
    difficulty: data.lesson.isBreakFixLesson ? difficultyMapping[data.type] : data.difficulty,
    duration: data.lesson.duration,
    videoId: data.lesson.videoId,
    isBreakFix: data.lesson.isBreakFixLesson,
    hasExercise: data.lesson.hasExperiment,
    since: data.lesson.since,
    events: {
      isConsumed: data.isConsumed,
      watchedVideo: data.watchedVideo,
      readTranscript: data.readTranscript,
    },
    ...overrides,
  };

  if (tournamentContent?.assessmentProps?.assessment) {
    details.assessment = tournamentContent.assessmentProps.assessment;
    details.question = tournamentContent.assessmentProps.question;
    details.showBeforeSurvey = false;
  } else if (!quizRedesign) {
    const assessment = await getAssessment(details.id);
    details.assessment = assessment.assessment;
    details.question = assessment.question;
    details.showBeforeSurvey = assessment.assessment.showBeforeSurvey;
    details.nextLesson = assessment.nextLesson;
  } else {
    const { assessment, questionsData } = await getQuiz(data.lesson.lessonKey);
    details.assessment = assessment;
    details.questions = questionsData;
    details.showBeforeSurvey = assessment.showBeforeSurvey;
    details.nextLesson = assessment.nextLesson;
  }

  details.experimentPassed = tournamentContent?.experimentPassed;
  details.lessonPassed = tournamentContent?.lessonPassed;

  return details;
}

/**
 * useLessonLoader Hook
 *
 * Custom hook to fetch and manage lesson details from multiple sources.
 * Combines data from Rails API and HackEdu content API for break/fix lessons.
 *
 * @param {string} lessonKey - The unique key identifying the lesson
 * @param {Object|null} tournamentContent - In a tournament context, the tournament lesson information
 * @param {Object|null} exerciseContent - Exercise details passed in for tournaments or exercise-only lessons
 * @returns {Object} Hook state and methods
 * @returns {boolean} isLoading - Whether lesson data is currently being fetched
 * @returns {boolean} isLessonLoaded - Whether lesson data has been successfully loaded
 * @returns {Object} lessonDetails - The combined lesson details object
 * @returns {Function} setLessonDetails - State setter for lesson details
 */
export function useLessonLoader(lessonKey, tournamentContent = null, exerciseContent = null) {
  const contentClient = getContentClient();
  const experimentClient = getExperimentClient();
  const [lessonDetails, setLessonDetails] = useState(null);
  const exerciseReservationRef = useRef(null);
  const { useGetLessonPublic } = useFlags();
  const { quizRedesign } = useFlags();

  // If we have an exercise, make the reservation now and set an interval to re-reserve every 10 minutes
  useEffect(() => {
    if (lessonDetails?.hasExercise && lessonDetails?.variant) {
      experimentClient.prepareTest(
        lessonDetails.variant.lessonKey,
        lessonDetails.variant.lessonVersion,
        lessonDetails.variant.currentVariant
      );

      exerciseReservationRef.current = setInterval(
        () => {
          experimentClient.prepareTest(
            lessonDetails.variant.lessonKey,
            lessonDetails.variant.lessonVersion,
            lessonDetails.variant.currentVariant
          );
        },
        1000 * 10 * 60
      );

      return () => clearInterval(exerciseReservationRef.current);
    }
  }, [lessonDetails?.hasExercise, lessonDetails?.variant]);

  // Load lesson details on mount or when lessonKey changes
  useEffect(() => {
    setLessonDetails(null);

    async function doLoad() {
      if (!lessonKey && !exerciseContent) {
        return;
      }

      let combinedLessonDetails = {};
      let svcContentDetails = {};

      if (lessonKey) {
        try {
          const railsDetails = await fetchLessonAttempt(lessonKey);
          if (useGetLessonPublic) {
            const response = await contentClient.getLessonPublic(lessonKey);
            svcContentDetails = convertLessonDetails(response.data);
          } else if (railsDetails.lesson.isBreakFixLesson) {
            svcContentDetails = await contentClient.getLessonDetails(lessonKey);
          }
          combinedLessonDetails = await buildLessonDetails(
            railsDetails,
            svcContentDetails,
            tournamentContent,
            quizRedesign
          );
        } catch (error) {
          console.error("Error fetching lesson details:", error);
        }

        // Use exercise details from exerciseContent (i.e. tournament data) if available
        // otherwise fetch from Rails API
        if (combinedLessonDetails.hasExercise) {
          const exerciseDetails =
            exerciseContent?.experiment ?? (await fetchExerciseDetails(combinedLessonDetails.id));
          const variantDetails = await fetchExerciseVariant(exerciseDetails.experiment.id);
          exerciseDetails.variant = variantDetails;
          combinedLessonDetails = { ...combinedLessonDetails, ...exerciseDetails };
        }

        // Load resources
        try {
          const resources = await fetchLessonResources(combinedLessonDetails.id);
          if (resources?.length > 0) {
            combinedLessonDetails.resources = resources;
          }
        } catch (error) {
          console.error("Error fetching lesson resources:", error);
        }
      } else if (exerciseContent !== null) {
        // We're in an exercise-only context
        const { experiment, steps } = exerciseContent;
        const { name: title } = experiment;
        const variant = await fetchExerciseVariant(experiment.id);
        const staticDetails = {
          type: "Exercise",
          level: "Advanced",
          duration: 10,
          hasExercise: true,
        };
        combinedLessonDetails = { title, experiment, variant, steps, ...staticDetails };
      }

      // Update state with combined details
      setLessonDetails(combinedLessonDetails);
    }

    doLoad();
  }, [lessonKey, tournamentContent, exerciseContent]);

  return {
    lessonDetails,
    setLessonDetails,
  };
}
